/**
 * useDataCache — Cache centralizado para DoctorCar
 *
 * Estrategia:
 * 1. Memoria (Map): instantáneo, se pierde al recargar
 * 2. localStorage: persiste entre sesiones, máximo ~5MB por key
 * 3. TTL dinámico: los datos cambian los lunes → TTL hasta el próximo lunes
 *
 * Uso:
 *   const cache = useDataCache();
 *   const data = await cache.get('catalogo', fetchFn);
 */

// ── Cache en memoria (singleton por tab del browser) ─────────
const memCache = new Map(); // { key: { data, ts, ttl } }

// ── Helpers ──────────────────────────────────────────────────

/**
 * Calcula los segundos hasta el próximo lunes a las 06:00 AM.
 * Asumiendo que la carga semanal ocurre los lunes.
 * TTL mínimo 1 hora, máximo 7 días.
 */
function ttlHastaProximoLunes() {
    const ahora = new Date();
    const diaSem = ahora.getDay(); // 0=domingo, 1=lunes, ..., 6=sábado
    const diasHastaLunes = diaSem === 1
        ? (ahora.getHours() < 10 ? 0 : 7) // si es lunes y antes de las 10am, posible que no cargó aún
        : (8 - diaSem) % 7 || 7;

    const proximoLunes = new Date(ahora);
    proximoLunes.setDate(ahora.getDate() + diasHastaLunes);
    proximoLunes.setHours(10, 0, 0, 0); // 10am del próximo lunes

    const ms = proximoLunes.getTime() - ahora.getTime();
    const segundos = Math.floor(ms / 1000);

    // Mínimo 1h, máximo 7 días
    return Math.max(3600, Math.min(segundos, 7 * 86400));
}

function ahoraTs() {
    return Math.floor(Date.now() / 1000);
}

// ── localStorage helpers ─────────────────────────────────────
const LS_PREFIX = 'dc_cache_v1_';
const LS_MAX_SIZE = 4 * 1024 * 1024; // 4MB por clave

function lsGet(key) {
    try {
        const raw = localStorage.getItem(LS_PREFIX + key);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (!entry || !entry.ts || !entry.ttl) return null;
        if (ahoraTs() > entry.ts + entry.ttl) {
            localStorage.removeItem(LS_PREFIX + key);
            return null;
        }
        return entry;
    } catch {
        return null;
    }
}

function lsSet(key, data, ttl) {
    try {
        const entry = { data, ts: ahoraTs(), ttl };
        const str = JSON.stringify(entry);
        if (str.length > LS_MAX_SIZE) {
            console.warn(`[cache] ${key} demasiado grande para localStorage (${(str.length / 1024).toFixed(0)}KB), solo en memoria`);
            return;
        }
        localStorage.setItem(LS_PREFIX + key, str);
    } catch (e) {
        // QuotaExceededError — limpiar cache viejo
        console.warn('[cache] localStorage lleno, limpiando cache antiguo');
        lsClear();
    }
}

function lsClear(prefixFilter = '') {
    try {
        const keys = Object.keys(localStorage).filter(k =>
            k.startsWith(LS_PREFIX) && k.includes(prefixFilter)
        );
        keys.forEach(k => localStorage.removeItem(k));
        console.log(`[cache] Limpiados ${keys.length} entradas del localStorage`);
    } catch { /* noop */ }
}

function lsStats() {
    try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(LS_PREFIX));
        const totalBytes = keys.reduce((sum, k) => sum + (localStorage.getItem(k)?.length || 0), 0);
        return { keys: keys.length, sizeKB: Math.round(totalBytes / 1024) };
    } catch {
        return { keys: 0, sizeKB: 0 };
    }
}

// ── API principal ─────────────────────────────────────────────

/**
 * get(key, fetchFn, options)
 * 
 * Busca en: memoria → localStorage → Firestore (fetchFn)
 * 
 * @param {string} key         - Identificador único del dato
 * @param {Function} fetchFn   - Async function que retorna los datos frescos
 * @param {Object} options     - { ttl, forceRefresh, skipLS }
 */
async function get(key, fetchFn, options = {}) {
    const { forceRefresh = false, skipLS = false } = options;
    const ttl = options.ttl || ttlHastaProximoLunes();
    const now = ahoraTs();

    // 1. Memoria
    if (!forceRefresh) {
        const mem = memCache.get(key);
        if (mem && now < mem.ts + mem.ttl) {
            console.log(`[cache] HIT memoria: ${key} (expira en ${Math.round((mem.ts + mem.ttl - now) / 60)}min)`);
            return mem.data;
        }
    }

    // 2. localStorage
    if (!forceRefresh && !skipLS) {
        const ls = lsGet(key);
        if (ls) {
            console.log(`[cache] HIT localStorage: ${key} (expira en ${Math.round((ls.ts + ls.ttl - now) / 60)}min)`);
            // Promover a memoria
            memCache.set(key, ls);
            return ls.data;
        }
    }

    // 3. Fetch fresco
    console.log(`[cache] MISS: ${key} — fetching...`);
    const t0 = Date.now();
    const data = await fetchFn();
    const latency = Date.now() - t0;
    console.log(`[cache] SET: ${key} (${latency}ms, TTL ${Math.round(ttl / 3600)}h)`);

    const entry = { data, ts: now, ttl };
    memCache.set(key, entry);
    if (!skipLS) lsSet(key, data, ttl);

    return data;
}

/**
 * invalidate(key) — Borra una entrada del cache
 */
function invalidate(key) {
    memCache.delete(key);
    try { localStorage.removeItem(LS_PREFIX + key); } catch { /* noop */ }
    console.log(`[cache] Invalidado: ${key}`);
}

/**
 * invalidateAll() — Borra todo el cache
 */
function invalidateAll() {
    memCache.clear();
    lsClear();
    console.log('[cache] Cache completo limpiado');
}

/**
 * stats() — Información del estado del cache
 */
function stats() {
    const now = ahoraTs();
    const mem = [...memCache.entries()].map(([key, v]) => ({
        key,
        expiresIn: Math.max(0, v.ts + v.ttl - now),
        valid: now < v.ts + v.ttl,
        items: Array.isArray(v.data) ? v.data.length : typeof v.data === 'object' ? Object.keys(v.data).length : 1,
    }));
    return { memory: mem, localStorage: lsStats() };
}

// ── Hook React ────────────────────────────────────────────────

/**
 * useDataCache() — Hook que expone la API del cache
 * 
 * Ejemplo de uso en un componente:
 * 
 * const cache = useDataCache();
 * 
 * // En useEffect o función async:
 * const modelos = await cache.get('catalogo_ultima_carga', async () => {
 *   const snap = await getDocs(query(collection(db, 'modelos'), ...));
 *   return snap.docs.map(d => ({ id: d.id, ...d.data() }));
 * });
 */
export function useDataCache() {
    return { get, invalidate, invalidateAll, stats };
}

// ── Claves de cache estándar ──────────────────────────────────
// Usar estas constantes para evitar typos entre componentes

export const CACHE_KEYS = {
    // _v2: la migración a Neon cambió la forma de los datos cacheados bajo
    // estas claves (antes arrays de docs de Firestore, ahora los objetos que
    // devuelven las Cloud Functions). Cambiar la clave evita que localStorage
    // (TTL de hasta 7 días) sirva un valor viejo con una forma distinta.
    CATALOGO: 'catalogo_ultima_carga_v2',
    ULTIMA_FECHA: 'ultima_fecha_carga',
    VARIACIONES: 'variaciones_semanales_v2',
    BUSQUEDA_MODELOS: 'busqueda_modelos_pool',
    SEGMENTO: (cat) => `segmento_${cat}`,
    SPECS_MARCA: (marca) => `specs_${marca}`,
};

// ── Export directo (para usar sin hook) ──────────────────────
export const dataCache = { get, invalidate, invalidateAll, stats };